<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'MasterData',
  },
})

const ability = useAbility()

const items = ref([])
const loading = ref(false)
const totalItems = ref(0)
const page = ref(1)
const perPage = ref(15)
const search = ref('')

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

const formDefault = () => ({
  name: '', code: '', unit_id: null, facility_id: null,
  category: '', purchase_date: '', condition: 'baik', is_active: true,
})

const form = ref(formDefault())

const conditionOptions = [
  { title: 'Baik', value: 'baik' },
  { title: 'Rusak', value: 'rusak' },
  { title: 'Perlu Kalibrasi', value: 'perlu_kalibrasi' },
]

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const fetchItems = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value) query.search = search.value

    const response = await useApi('/api/v1/master-data/equipments', { query }).get()
    const data = response?.data ?? response

    items.value = Array.isArray(data) ? data : (data?.data ?? [])
    totalItems.value = data?.meta?.total ?? data?.total ?? 0
  } catch (e) {
    console.error('Fetch equipments error:', e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchItems)
watch(search, () => { page.value = 1; fetchItems() })
watch(page, fetchItems)

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Alat'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    name: item.name,
    code: item.code,
    unit_id: item.unit_id,
    facility_id: item.facility_id,
    category: item.category ?? '',
    purchase_date: item.purchase_date ?? '',
    condition: item.condition,
    is_active: item.is_active,
  }
  errors.value = {}
  dialogTitle.value = 'Edit Alat'
  dialog.value = true
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(editingId.value ? `/api/v1/master-data/equipments/${editingId.value}` : '/api/v1/master-data/equipments')
    const response = editingId.value ? await api.put({ body: form.value }) : await api.post({ body: form.value })
    const saved = response?.data ?? response

    if (editingId.value) {
      const idx = items.value.findIndex(i => i.id === editingId.value)
      if (idx !== -1) items.value[idx] = saved
    } else {
      fetchItems()
    }
    dialog.value = false
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save equipment error:', e)
  } finally {
    saving.value = false
  }
}

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/master-data/equipments/${deletingItem.value.id}`).delete()
    deleteDialog.value = false
    fetchItems()
  } catch (e) {
    console.error('Delete equipment error:', e)
  } finally {
    deleting.value = false
  }
}

const conditionColor = value => ({ baik: 'success', rusak: 'error', perlu_kalibrasi: 'warning' }[value] ?? 'secondary')
const conditionLabel = value => conditionOptions.find(c => c.value === value)?.title ?? value

const headers = [
  { title: 'Kode', key: 'code', width: 120 },
  { title: 'Nama', key: 'name' },
  { title: 'Unit', key: 'unit' },
  { title: 'Fasilitas', key: 'facility' },
  { title: 'Kondisi', key: 'condition', align: 'center', width: 140 },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 120 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Alat Kesehatan
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master data alat kesehatan/peralatan
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Alat
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VTextField
          v-model="search"
          placeholder="Cari nama atau kode..."
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          density="compact"
          style="max-width: 320px;"
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <template #item.code="{ item }">
          <code>{{ item.code }}</code>
        </template>
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.facility="{ item }">
          {{ item.facility?.name ?? '-' }}
        </template>
        <template #item.condition="{ item }">
          <VChip
            size="small"
            :color="conditionColor(item.condition)"
          >
            {{ conditionLabel(item.condition) }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'MasterData')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data
            </p>
          </div>
        </template>
      </VDataTable>
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">Total: {{ totalItems }}</span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="640"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.name"
                label="Nama Alat *"
                :error-messages="errors.name"
                autofocus
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.code"
                label="Kode/No. Aset *"
                :error-messages="errors.code"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <MasterDataPicker
                v-model="form.unit_id"
                entity="units"
                label="Unit"
                :error-messages="errors.unit_id"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <MasterDataPicker
                v-model="form.facility_id"
                entity="facilities"
                label="Fasilitas"
                :error-messages="errors.facility_id"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.category"
                label="Kategori"
                :error-messages="errors.category"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VTextField
                v-model="form.purchase_date"
                label="Tanggal Pembelian"
                type="date"
                :error-messages="errors.purchase_date"
              />
            </VCol>
            <VCol cols="12">
              <VSelect
                v-model="form.condition"
                :items="conditionOptions"
                label="Kondisi *"
                :error-messages="errors.condition"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Alat?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.name }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
